// src/pages/VehicleV2/VehicleSpareDeductionListing.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaTrash, FaSave, FaTimes, FaChevronDown, FaChevronUp } from "react-icons/fa";

const LIST_API = "https://nissi-india.com/api/spareDeductionListing";
const ENTRY_API = "https://nissi-india.com/api/spareDeduction";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const pad2 = (n) => String(n).padStart(2, "0");

const ddmmyyyyToYmd = (ddmmyyyy) => {
  const v = toStr(ddmmyyyy);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const ymdToDDMMYYYY = (ymd) => {
  const v = toStr(ymd);
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const todayToYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const todayToDDMMYYYY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isValidDDMMYYYY = (s) => {
  const v = toStr(s);
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

const clampPercentage = (raw) => {
  let v = toStr(raw).replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join("")}`;
  const [a, b] = v.split(".");
  if (typeof b === "string") v = `${a}.${b.slice(0, 2)}`;
  if (v.length > 6) v = v.slice(0, 6);
  return v;
};

const fmtDate = (v) => {
  const s = toStr(v);
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const VehicleSpareDeductionListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const todayYmd = useMemo(() => todayToYMD(), []);

  const [showForm, setShowForm] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [spareId, setSpareId] = useState(0);
  const [subtypes, setSubtypes] = useState([]);
  const [subtypeId, setSubtypeId] = useState("Select");
  const [percentage, setPercentage] = useState("");
  const [entryDate, setEntryDate] = useState(todayToDDMMYYYY());
  const isEdit = spareId > 0;

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);
  const rowId = (r) => toInt(r.id || r.fld_sparedeductionperid);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const resetForm = useCallback(() => {
    setSpareId(0);
    setSubtypeId("Select");
    setPercentage("");
    setEntryDate(todayToDDMMYYYY());
  }, []);

  const loadSubtypes = useCallback(
    async (editMode = false) => {
      try {
        const { data } = await axios.get(`${ENTRY_API}/subtypes`, {
          params: { mode: editMode ? "edit" : "add" },
          headers: commonHeaders,
        });
        const list = Array.isArray(data?.subtypes) ? data.subtypes : [];
        setSubtypes(
          [{ id: "Select", name: "Select" }].concat(
            list.map((x) => ({ id: String(x.id), name: String(x.name) }))
          )
        );
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load sub types");
        setSubtypes([{ id: "Select", name: "Select" }]);
      }
    },
    [commonHeaders]
  );

  const fetchList = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;
      try {
        setLoading(true);
        const { data } = await axios.get(`${LIST_API}/list`, {
          params: { loginUserId, page: nextPage, limit: nextLimit, search: nextSearch },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load list");
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(toInt(data?.total, 0));
        setSelectedIds([]);
        setPage(nextPage);
        setLimit(nextLimit);
      } catch (err) {
        setRows([]);
        setTotal(0);
        toast.error(err?.response?.data?.message || err?.message || "Server error");
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, loginUserId, commonHeaders]
  );

  useEffect(() => {
    loadSubtypes(false);
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const handleEdit = async (row) => {
    const id = rowId(row);
    if (!id) return;
    setShowForm(true);
    setSpareId(id);
    await loadSubtypes(true);
    try {
      const { data } = await axios.get(`${ENTRY_API}/view`, {
        params: { spareId: id },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Failed to load record");
      const r = data?.data || {};
      setSubtypeId(r.subtypeId != null ? String(r.subtypeId) : "Select");
      setPercentage(r.percentage != null ? String(r.percentage) : "");
      setEntryDate(toStr(r.entryDate) || fmtDate(row.fld_entrydate));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load record");
    }
  };

  const validate = () => {
    if (!subtypeId || String(subtypeId) === "Select") {
      toast.error("Please select Vehicle Sub Type");
      return false;
    }
    if (!toStr(entryDate) || !isValidDDMMYYYY(entryDate)) {
      toast.error("Please select a valid Effect Date");
      return false;
    }
    const [dd, mm, yyyy] = entryDate.split("/").map(Number);
    const selectedDate = new Date(yyyy, mm - 1, dd);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate.getTime() > today.getTime()) {
      toast.error("Select Current Date or Previous date");
      return false;
    }
    const pct = Number(percentage);
    if (!Number.isFinite(pct) || pct <= 0) {
      toast.error("Percentage should be greater than 0");
      return false;
    }
    if (pct > 100) {
      toast.error("Percentage should not be greater than 100");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${ENTRY_API}/save`,
        {
          spareId: spareId || 0,
          subtypeId: toInt(subtypeId, 0),
          entryDate: ddmmyyyyToYmd(entryDate),
          percentage: Number(percentage),
          loginUserId,
          branchId,
          branchType: branch,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Save failed");
      toast.success(data?.message || "Saved successfully");
      resetForm();
      await loadSubtypes(false);
      await fetchList({ page });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return toast.warn("Please select at least one record to delete");
    if (!window.confirm(`Delete ${selectedIds.length} selected record(s)?`)) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${LIST_API}/delete`,
        { ids: selectedIds, loginUserId },
        { headers: commonHeaders }
      );
      if (data?.success) toast.success(data?.message || "Deleted Successfully");
      else toast.warn(data?.message || "Delete failed");
      if (data?.inUseIds?.length) toast.info(`${data.inUseIds.length} record(s) are in use`);
      if (selectedIds.includes(spareId)) resetForm();
      const deletedCount = Array.isArray(data?.deletedIds) ? data.deletedIds.length : 0;
      const afterPages = Math.max(1, Math.ceil(Math.max(0, total - deletedCount) / limit));
      await fetchList({ page: Math.min(page, afterPages) });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const idsOnPage = rows.map(rowId).filter((x) => x > 0);
  const allSelected = idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id));

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SPARE DEDUCTION</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Spare Deduction Percent
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
            
              <button
                onClick={() => setShowForm((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showForm && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  {isEdit ? "Edit Spare Deduction" : "Add Spare Deduction"}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={resetForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2"><FaTimes /> Clear</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                      saving ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass}`}
                  >
                    <FaSave />
                    {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Vehicle Sub Type *</label>
                  <select
                    value={subtypeId}
                    onChange={(e) => setSubtypeId(e.target.value)}
                    disabled={saving || isEdit}
                    className={inputClass}
                  >
                    {subtypes.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Percent *</label>
                  <input
                    type="text"
                    value={percentage}
                    onChange={(e) => setPercentage(clampPercentage(e.target.value))}
                    className={inputClass}
                    placeholder="Enter percentage..."
                    disabled={saving}
                    inputMode="decimal"
                    maxLength={6}
                  />
                </div>
                <div>
                  <label className={labelClass}>Effect Date *</label>
                  <input
                    type="date"
                    value={ddmmyyyyToYmd(entryDate)}
                    max={todayYmd}
                    onChange={(e) => setEntryDate(ymdToDDMMYYYY(e.target.value))}
                    className={inputClass}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSearch("");
                      fetchList({ page: 1, search: "" });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => fetchList({ page: 1, search })}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchList({ page: 1, search })}
                className={inputClass}
                placeholder="Search subtype / date / percentage..."
              />
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>Loading...</div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No Records Found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Sub Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Deduction %</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => setSelectedIds(allSelected ? [] : idsOnPage)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => {
                      const id = rowId(r);
                      return (
                        <tr
                          key={id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } ${spareId === id ? (isDark ? "ring-1 ring-blue-400" : "bg-blue-50") : ""} ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td className="px-6 py-4 text-sm">{r.slno ?? (page - 1) * limit + idx + 1}</td>
                          <td className="px-6 py-4 text-sm">{fmtDate(r.fld_entrydate)}</td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {r.fld_vehicle_subtypename || ""}
                          </td>
                          <td className="px-6 py-4 text-sm">{r.fld_percentage ?? ""}</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() =>
                                setSelectedIds((p) =>
                                  p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
                                )
                              }
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="p-4">
                  <button
                    onClick={handleDelete}
                    disabled={!selectedIds.length}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                      selectedIds.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{totalPages}</b> | Total: <b>{total}</b>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={limit}
                onChange={(e) => fetchList({ page: 1, limit: toInt(e.target.value, 10) })}
                className={inputClass.replace("w-full ", "w-auto ")}
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <button
                disabled={page <= 1}
                onClick={() => fetchList({ page: page - 1 })}
                className={`px-3 py-2 border rounded-lg ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""} ${
                  isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => fetchList({ page: page + 1 })}
                className={`px-3 py-2 border rounded-lg ${
                  page >= totalPages ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSpareDeductionListing;